import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Award, 
  Heart, 
  Lightbulb, 
  Users, 
  Target, 
  Sparkles,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Trophy,
  Star
} from 'lucide-react';
import { companyApiService, CompanyProfile } from '../services/companyApi';
import ServicesCarousel from '../components/ServicesCarousel';
import PacksCarousel from '../components/PacksCarousel';

const AboutIrisSoftechPage: React.FC = () => {
  const navigate = useNavigate();
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  const loadCompanyProfile = async () => {
    try {
      const profile = await companyApiService.getCompanyProfile();
      setCompanyProfile({
        ...profile,
        email: 'softechiris@gmail.com',
        phone: '771742350',
        address: 'Medina RUE 27 X 22 ENSUITE'
      });
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      // Utiliser des données par défaut en cas d'erreur
      setCompanyProfile({
        name: 'IRIS SOFTECH',
        slogan: 'Solutions Digitales Innovantes',
        email: 'softechiris@gmail.com',
        phone: '771742350',
        website: 'www.irissoftech.com',
        address: 'Medina RUE 27 X 22 ENSUITE'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Header avec navigation */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100/50 sticky top-0 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour à l'accueil</span>
            </button>
            
            <div className="flex items-center space-x-3">
              {companyProfile?.logo_url && (
                <img 
                  src={companyProfile.logo_url} 
                  alt="Logo" 
                  className="w-10 h-10 object-contain"
                />
              )}
              <div className="text-right">
                <h1 className="text-lg font-bold text-gray-900">{companyProfile?.name}</h1>
                <p className="text-sm text-gray-600">{companyProfile?.slogan}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="relative inline-block mb-8">
            {companyProfile?.logo_url ? (
              <img 
                src={companyProfile.logo_url} 
                alt={`Logo ${companyProfile.name}`}
                className="w-24 h-24 mx-auto object-contain mb-6"
              />
            ) : (
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-12 h-12 text-white" />
              </div>
            )}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <Star className="w-3 h-3 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              {companyProfile?.name}
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {companyProfile?.slogan}
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            {companyProfile?.email && (
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>{companyProfile.email}</span>
              </div>
            )}
            {companyProfile?.phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{companyProfile.phone}</span>
              </div>
            )}
            {companyProfile?.website && (
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>{companyProfile.website}</span>
              </div>
            )}
            {companyProfile?.address && (
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{companyProfile.address}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Histoire d'IRIS */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Notre Histoire</h2>
            </div>
            
            <div className="space-y-6 text-gray-800 leading-relaxed">
              <p>
                IRIS SOFTECH est née d'une vision audacieuse : 
                démocratiser l'accès aux technologies digitales pour tous les entrepreneurs africains. 
                Notre histoire commence avec une passion pour l'innovation et un engagement profond 
                envers l'excellence.
              </p>
              
              <p>
                Notre approche se distingue par une compréhension fine des enjeux technologiques 
                contemporains et une capacité à transformer les défis en opportunités d'innovation. 
                Nous créons des solutions sur mesure qui répondent aux besoins spécifiques de chaque client.
              </p>
              
              <p>
                Chaque projet que nous réalisons est une nouvelle opportunité de repousser les 
                limites de l'innovation, tout en gardant à cœur les valeurs humaines qui nous 
                définissent : l'écoute, la créativité et l'excellence.
              </p>
            </div>
          </motion.div>

          {/* Histoire de la Fondatrice */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 shadow-lg border border-pink-100"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Notre Fondatrice</h2>
            </div>
            
            <div className="space-y-6 text-gray-800 leading-relaxed">
              <div className="flex items-center space-x-2 mb-4">
                <Calendar className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-600">2019 - Les Débuts</span>
              </div>
              
              <p>
                L'aventure IRIS SOFTECH a commencé avec 
                un projet révolutionnaire : SOS Sang, une plateforme digitale innovante 
                qui a marqué les esprits et ouvert la voie à notre expertise actuelle.
              </p>
              
              <div className="bg-white/60 rounded-2xl p-4 border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  <span className="font-semibold text-gray-900">3ème Miss Geek Africa 2019</span>
                </div>
                <p className="text-sm text-gray-700">
                  Une reconnaissance internationale qui a validé notre approche innovante 
                  et notre engagement pour l'impact social à travers la technologie.
                </p>
              </div>
              
              <p>
                Cette expérience fondatrice a forgé notre ADN : 
                allier innovation technologique et impact social positif, en créant 
                des solutions qui transforment réellement la vie des utilisateurs.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Vision Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-blue-50 via-white to-purple-50 rounded-3xl p-12 shadow-lg border border-blue-100 mb-20"
        >
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Lightbulb className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Notre Vision</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Façonner l'avenir digital de l'Afrique en accompagnant chaque entrepreneur 
              vers l'excellence technologique
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-700 leading-relaxed">
                Nous repoussons constamment les limites de la technologie pour créer 
                des solutions avant-gardistes qui anticipent les besoins de demain.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Accompagnement</h3>
              <p className="text-gray-700 leading-relaxed">
                Chaque entrepreneur mérite un partenaire technologique qui comprend 
                ses défis et l'accompagne vers la réussite avec expertise et bienveillance.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Excellence</h3>
              <p className="text-gray-700 leading-relaxed">
                L'excellence n'est pas un accident, c'est le résultat d'une préparation 
                rigoureuse, d'un travail acharné et d'une passion inébranlable.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Espace pour contenu personnalisé */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="bg-white/70 backdrop-blur-sm rounded-3xl p-12 shadow-lg border border-white/50 mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              L'Esprit <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">IRIS</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto"></div>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed space-y-6">
            <p className="text-xl text-center text-gray-700 mb-8 italic">
              "Chez IRIS SOFTECH, nous ne créons pas seulement des solutions digitales, 
              nous tissons des ponts vers l'avenir."
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 text-base">
              <div>
                <p>
                  Notre approche unique combine expertise technique de pointe et 
                  compréhension profonde des enjeux business. Nous croyons que 
                  chaque ligne de code doit servir un objectif précis : faire grandir votre entreprise.
                </p>
                
                <p>
                  L'innovation chez IRIS SOFTECH ne se limite pas aux technologies que nous utilisons. 
                  Elle réside dans notre capacité à transformer des idées complexes en solutions 
                  simples et élégantes qui révolutionnent la façon dont nos clients 
                  interagissent avec leurs marchés.
                </p>
              </div>
              
              <div>
                <p>
                  Nous sommes fiers d'être les architectes de la transformation digitale 
                  africaine. Chaque projet est une opportunité de démontrer que l'excellence 
                  technologique n'a pas de frontières géographiques.
                </p>
                
                <p>
                  Notre engagement va au-delà de la livraison de projets. Nous construisons des 
                  partenariats durables basés sur la confiance mutuelle, 
                  l'innovation continue et la croissance partagée.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Carrousel Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nos Services d'Excellence</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto"></div>
          </div>
          <ServicesCarousel />
        </motion.div>

        {/* Carrousel Packs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nos Packs Premium</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full mx-auto"></div>
          </div>
          <PacksCarousel />
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-6">Prêt à écrire votre histoire digitale ?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Rejoignez les entrepreneurs visionnaires qui ont choisi IRIS SOFTECH 
              pour transformer leurs ambitions en réalité digitale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/devisrequest')}
                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Démarrer mon projet
              </button>
              <button 
                onClick={() => navigate('/services')}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Découvrir nos services
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutIrisSoftechPage;
